import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { getContextNavigation } from '@plone/volto/actions/contextNavigation/contextNavigation';
import type { ContextNavigationParams } from './params';

interface WithContextNavigationProps {
  location: { pathname: string };
  params?: ContextNavigationParams;
  [key: string]: any;
}

/**
 * O `withContentNavigation` do Volto, sem buscar de novo o que já está no
 * Redux (o SSR carrega o menu da página e a hidratação não repete a chamada)
 * e sem esvaziar o menu enquanto o da página nova carrega.
 */
export function withContextNavigation<P extends object>(
  WrappedComponent: React.ComponentType<P & { navigation?: any }>,
) {
  function Wrapped(props: P & WithContextNavigationProps) {
    const { location, params = {} } = props;
    const pathname = getBaseUrl(location.pathname);
    const qs = Object.keys(params)
      .sort()
      .map((key) => `expand.contextnavigation.${key}=${params[key]}`)
      .join('&');
    const key = `${pathname}${pathname.endsWith('/') ? '' : '/'}@contextnavigation${qs ? `?${qs}` : ''}`;

    const dispatch = useDispatch();
    const entry = useSelector((state: any) => state.contextNavigation?.[key]);

    // Depois de um erro não tenta de novo sozinho, senão entra em laço.
    const needsFetch = !entry;
    useEffect(() => {
      if (needsFetch) {
        dispatch(getContextNavigation(pathname, params) as any);
      }
      // `key` já resume `pathname` e `params`.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, needsFetch, dispatch]);

    // Ao trocar de página o menu anterior fica na tela até o novo chegar:
    // sumir e voltar faz a lateral encolher e pular.
    const lastData = useRef(entry?.data);
    if (entry?.data) {
      lastData.current = entry.data;
    }
    const navigation = entry?.error
      ? undefined
      : entry?.data ?? lastData.current;

    return <WrappedComponent {...props} navigation={navigation} />;
  }

  Wrapped.displayName = `WithContextNavigation(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return Wrapped;
}
